/**
 * 프론트엔드 인증/권한 타입 정의
 * 백엔드 FIRESTORE_SCHEMA.md 및 functions/src/types와 일치
 */

import { Timestamp } from 'firebase/firestore';

// ========== Permission & Role Types ==========

/**
 * 권한 문자열
 * 예: "hr.employees.view", "attendance.records.create"
 */
export type Permission = string;

/**
 * 역할 타입
 */
export enum RoleType {
  SUPER_ADMIN = 'SUPER_ADMIN',
  HR_ADMIN = 'HR_ADMIN',
  TEAM_LEADER = 'TEAM_LEADER',
  EMPLOYEE = 'EMPLOYEE',
  CUSTOM = 'CUSTOM',
}

/**
 * 역할 문서 (auth_roles)
 */
export interface Role {
  id: string;
  name: string;
  type: RoleType;
  description: string;
  permissions: Permission[];
  isSystem: boolean;
  status: 'active' | 'inactive';
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdBy: string;
  updatedBy: string;
}

/**
 * 역할 할당 대상 타입
 */
export enum AssignmentTargetType {
  USER = 'user',
  POSITION = 'position',
  TEAM = 'team',
  ORG_UNIT = 'orgUnit',
}

/**
 * 역할 할당 문서 (auth_roleAssignments)
 */
export interface RoleAssignment {
  id: string;
  roleId: string;
  targetType: AssignmentTargetType;
  targetId: string;
  status: 'active' | 'inactive';
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdBy: string;
  updatedBy: string;
}

// ========== User Types ==========

/**
 * 사용자 프로필 (Firestore users 컬렉션)
 */
export interface UserProfile {
  id: string; // Firebase Auth UID
  email: string;
  displayName: string;
  photoURL?: string;

  // 직원 정보
  employeeNumber?: string;
  positionId?: string;
  employmentTypeId?: string;
  orgUnitId?: string;
  teamId?: string;
  companyCode?: string; // V170.1: 회사 코드 (KR, CN 등)

  // 연락처
  phoneNumber?: string;
  mobileNumber?: string;

  // 입사/퇴사
  joinDate?: Timestamp;
  resignDate?: Timestamp;

  // V72: 퇴사 관련 필드 (백엔드 user.types.ts와 동기화)
  /** 퇴사일 (YYYY-MM-DD), 마지막 근무일 */
  leaveDate?: string;
  /** Firebase Auth 비활성화 일시 */
  authDisabledAt?: Timestamp;

  status: 'active' | 'inactive' | 'resigned' | 'on_leave' | 'terminated';
  metadata?: Record<string, any>;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdBy: string;
  updatedBy: string;
}

/**
 * 역할과 권한을 포함한 사용자 (useCurrentUser 훅에서 사용)
 */
export interface UserWithRoles {
  profile: UserProfile;
  roles: string[]; // roleId 목록
  permissions: Permission[]; // 모든 역할의 권한을 합친 목록
}

// ========== Auth Context Types ==========

/**
 * useCurrentUser 훅 반환 타입
 */
export interface UseCurrentUserResult {
  user: UserWithRoles | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * useAuthorization 훅 반환 타입
 */
export interface UseAuthorizationResult {
  user: UserWithRoles | null;
  isLoading: boolean;
  hasRole: (roleId: string) => boolean;
  hasAnyRole: (roleIds: string[]) => boolean;
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
}
